import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { map, switchMap, take } from 'rxjs/operators';
import { of } from 'rxjs';

import { AuthStore } from '@store/auth.store';
import { AdminPermissionService } from '@services/admin-permission.service';

export const NO_ACCESS_PATH = '/admin/no-access';

/**
 * Opens a page only when the admin holds the route's `data.permission`.
 * Routes without one are open to any signed-in admin.
 *
 * Runs after `AuthGuard` on the same route and steps aside for a signed-out
 * visitor — that one is AuthGuard's to send to the login page. A refused admin
 * goes to the first page they can open instead, so landing on the Overview
 * after login without `dashboard.view` is not a dead end.
 */
export const permissionGuard: CanActivateFn = (route) => {
  const required = route.data?.['permission'] as string | undefined;
  if (!required) return true;

  const authStore = inject(AuthStore);
  const permissions = inject(AdminPermissionService);
  const router = inject(Router);
  const toastr = inject(ToastrService);

  return authStore.isAuthenticated$.pipe(
    take(1),
    switchMap(({ isAuthenticated, otpVerified }) => {
      if (!isAuthenticated || !otpVerified) return of(true);

      return permissions.load().pipe(
        map((state) => {
          if (AdminPermissionService.allows(state, required)) return true;

          const landing = AdminPermissionService.landingPage(state);
          // The Overview is where login lands, so being moved off it is not news.
          if (route.routeConfig?.path !== '') {
            toastr.warning("You don't have access to that page.");
          }
          return router.parseUrl(landing ?? NO_ACCESS_PATH);
        }),
      );
    }),
  );
};
